import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Info, MessagesSquare, Search, Send } from 'lucide-react';
import moment from 'moment';
import { toast } from 'sonner';
import {
  useSalesConversationQuery,
  useSalesConversationReadMutation,
  useSalesConversationSendMutation,
  useSalesConversationsQuery,
  useSalesWhatsAppCheckMutation,
} from '@/client/api/sales/sales';
import type { SalesConversationChannel, SalesConversationThread } from '@/client/types/sales';
import { useMailboxesQuery } from '@/client/api/inbox/inbox';
import { smsLineLabel, useSmsLinesQuery } from '@/client/api/inbox/smsLines';
import EmailEditor from '@/client/components/admin/Email/EmailEditor';
import { SignatureToggle } from '@/client/components/admin/Email/Signatures';
import { getApiErrorMessage } from '@/client/utils/errors';
import { SalesPageShell } from './components/SalesPageShell';

const CHANNELS: { id: SalesConversationChannel; label: string; style: string }[] = [
  { id: 'whatsapp', label: 'WhatsApp', style: 'bg-green-50 text-green-700 border-green-200' },
  { id: 'sms', label: 'SMS', style: 'bg-blue-50 text-blue-700 border-blue-200' },
  { id: 'email', label: 'Email', style: 'bg-purple-50 text-purple-700 border-purple-200' },
];
const LOGGED_STYLE = 'bg-gray-50 text-gray-600 border-gray-200';
const channelOf = (id: string) => CHANNELS.find((c) => c.id === id);
const isChannel = (id: string): id is SalesConversationChannel => Boolean(channelOf(id));
const kindOf = (c: string) => (c === 'email' ? 'email' : 'phone');

/** Who a send can go to (main line first, then named contacts), and which address each message used. */
type Recipient = { value: string; label: string };
type Thread = Omit<SalesConversationThread, 'messages'> & {
  recipients?: { phone: Recipient[]; email: Recipient[] };
  /** What a send can go from, with the floor's default. SMS has one sender. */
  senders?: Partial<Record<'whatsapp' | 'email', { default: string; options: { id: string; label: string }[] }>>;
  messages: (SalesConversationThread['messages'][number] & { peer?: string })[];
};
/** Sales writes from these only; the server enforces it (settings.SALES_MAILBOXES). */
const SALES_MAILBOXES = ['sales@feelyfeely.com', 'hello@feelyfeely.com'];
const shown = (kind: 'phone' | 'email', value: string) => (kind === 'phone' ? `+${value}` : value);

const messageTime = (at?: number) => {
  if (!at) return '';
  const when = moment.unix(at);
  return when.isSame(moment(), 'day') ? when.format('HH:mm') : when.format('D MMM, HH:mm');
};

/**
 * Conversations with prospects, on WhatsApp, SMS and email.
 *
 * The prospects' side of Communications > Inbox: the same messages, but only
 * for people in the sales pool, scoped by acting-as, and every send goes
 * through the outreach guards (do-not-contact, cooldown, consent, caps).
 * Two filters, as in the Communications inbox: the channel, then (email only)
 * the mailbox. `?channel=` and `?mailbox=` open it filtered; `?subject=`
 * opens a prospect, which is how the lead page and "start a conversation" reach it.
 */
const SalesConversationsPage: React.FC = () => {
  const [params, setParams] = useSearchParams();
  // A legacy comma list keeps its first channel.
  const filter = ((params.get('channel') || '').split(',').find(isChannel) || '') as SalesConversationChannel | '';
  const mailboxes = (useMailboxesQuery().data || []).filter((m) => SALES_MAILBOXES.includes(m.email));
  const smsLines = (useSmsLinesQuery().data?.lines || []).filter((l) => l.enabled);
  // A mailbox only narrows email, so it is dropped whenever the channel changes.
  const mailbox = filter === 'email' ? params.get('mailbox') || '' : '';
  const subjectId = params.get('subject') || undefined;
  const update = (next: { channel?: SalesConversationChannel | ''; mailbox?: string; subject?: string }) => {
    const channel = 'channel' in next ? next.channel : filter;
    const box = channel === 'email' ? ('mailbox' in next ? next.mailbox : 'channel' in next ? '' : mailbox) : '';
    const subject = 'subject' in next ? next.subject : subjectId;
    setParams({ ...(channel ? { channel } : {}), ...(box ? { mailbox: box } : {}), ...(subject ? { subject } : {}) }, { replace: true });
  };

  const [search, setSearch] = useState('');
  const term = search.trim().length >= 2 ? search.trim() : '';
  const list = useSalesConversationsQuery({
    ...(filter ? { channel: filter } : {}),
    ...(mailbox ? { mailbox } : {}),
    ...(term ? { search: term } : {}),
  });
  const conversations = list.data?.conversations || [];
  // Prospects nobody has written to yet, always offered, so starting a conversation is one click.
  const talking = new Set(conversations.map((c) => c.subject.id));
  // In-scope (pooled, yours when acting as a rep) prospects nobody has written to; the server picks them.
  const newProspects = (list.data?.unwritten || []).filter((s) => !talking.has(s.id));

  const thread = useSalesConversationQuery(subjectId);
  const data = thread.data as Thread | undefined;
  const prospect = data?.subject;
  const messages = data?.messages || [];
  const recipients = data?.recipients || { phone: [], email: [] };
  const senders = data?.senders || {};
  const { mutate: markRead } = useSalesConversationReadMutation();
  const sendMessage = useSalesConversationSendMutation();
  const recheck = useSalesWhatsAppCheckMutation();

  const [channel, setChannel] = useState<SalesConversationChannel>('whatsapp');
  const [body, setBody] = useState('');
  const [subjectLine, setSubjectLine] = useState('');
  const [html, setHtml] = useState('');
  const [draftKey, setDraftKey] = useState(0);
  // '' = the first address / the floor's Sales mailbox.
  const [to, setTo] = useState('');
  const [from, setFrom] = useState('');
  const [withSignature, setWithSignature] = useState(true);
  const endRef = useRef<HTMLDivElement | null>(null);
  // Opened from a conversation row: keep its channel. Otherwise use the prospect's preferred one.
  const channelPicked = useRef(false);

  const open = (id: string, on?: SalesConversationChannel, peer?: string) => {
    update({ subject: id });
    channelPicked.current = Boolean(on);
    if (on) setChannel(on);
    // Answer on the address the conversation is on.
    setTo(peer || '');
    setFrom('');
    setBody('');
    setHtml('');
    setSubjectLine('');
    setDraftKey((k) => k + 1);
  };

  useEffect(() => {
    if (subjectId) markRead(subjectId);
  }, [subjectId, markRead]);

  const preferred = prospect?.settings?.preferredChannel;
  useEffect(() => {
    if (preferred && !channelPicked.current) setChannel(preferred);
  }, [prospect?.id, preferred]);

  // Contact hours are advice (Accra = UTC): warn, never block.
  const hours = prospect?.settings?.contactHours;
  const nowHm = new Date().toISOString().slice(11, 16);
  const outsideHours =
    hours && (hours.start <= hours.end ? !(nowHm >= hours.start && nowHm < hours.end) : !(nowHm >= hours.start || nowHm < hours.end));

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, subjectId]);

  // The server runs the same guards a send would, without sending.
  const checks = thread.data?.channels || {};
  const check = (c: SalesConversationChannel) => checks[c] || { ok: false, error_description: 'Checking...' };
  const current = check(channel);
  const kind = kindOf(channel);
  const choices = recipients[kind];
  // A picked address that this channel cannot use falls back to the first one (the main line, when it has one).
  const target = choices.some((r) => r.value === to) ? to : choices[0]?.value || '';
  const fromChoices = channel === 'sms' ? undefined : senders[channel];
  const sender = from || fromChoices?.default || '';
  const labelOf = (k: 'phone' | 'email', value?: string) =>
    recipients[k].length > 1 ? recipients[k].find((r) => r.value === value)?.label : undefined;

  const send = () => {
    if (!subjectId || !body.trim() || sendMessage.isPending) return;
    sendMessage.mutate(
      {
        subjectId,
        channel,
        body,
        ...(channel === 'email' && subjectLine.trim() ? { subject: subjectLine.trim() } : {}),
        ...(channel === 'email' && html ? { html } : {}),
        ...(target ? { to: target } : {}),
        ...(from ? { from } : {}),
        ...(channel === 'email' && from ? { signature: withSignature } : {}),
      },
      {
        onSuccess: () => {
          setBody('');
          setHtml('');
          setDraftKey((k) => k + 1);
        },
        onError: (error) => toast.error(getApiErrorMessage(error, 'The message was not sent')),
      },
    );
  };

  const selectClass = 'w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-blue-500';
  const chip = (on: boolean) =>
    `px-2.5 py-1 rounded-full text-xs font-medium border ${
      on ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
    }`;

  return (
    <SalesPageShell
      title="Communications"
      description="Your conversations with prospects on WhatsApp, SMS and email"
      crumbs={[{ label: 'Sales', href: '/admin/sales' }, { label: 'Communications' }]}
      icon={MessagesSquare}
    >
      <div className="h-[calc(100vh-14rem)] min-h-[480px] flex border rounded-lg overflow-hidden bg-gray-50">
        <div className="w-80 bg-white border-r flex flex-col">
          <div className="p-4 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                aria-label="Search prospects"
                placeholder="Search prospects by name, phone or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="mt-3 flex flex-col gap-2">
              <select
                aria-label="Channel"
                value={filter}
                onChange={(e) => update({ channel: e.target.value as SalesConversationChannel | '' })}
                className={selectClass}
              >
                <option value="">All channels</option>
                {CHANNELS.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.label}
                  </option>
                ))}
              </select>
              {filter === 'email' && mailboxes.length > 0 && (
                <select aria-label="Mailbox" value={mailbox} onChange={(e) => update({ mailbox: e.target.value })} className={selectClass}>
                  <option value="">All mailboxes</option>
                  {mailboxes.map((m) => (
                    <option key={m.email} value={m.email}>
                      {m.email}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto">
            {list.isLoading && <p className="p-4 text-sm text-gray-500">Loading conversations...</p>}
            {list.isError && <p className="p-4 text-sm text-red-600">Could not load conversations.</p>}
            {!list.isLoading && !list.isError && conversations.length === 0 && newProspects.length === 0 && (
              <p className="p-4 text-sm text-gray-500">
                {term ? (
                  'No prospects match.'
                ) : (
                  <>
                    No conversations yet.{' '}
                    <Link to="/admin/sales/prospects" className="text-blue-600 hover:underline">
                      Claim prospects
                    </Link>{' '}
                    to start one.
                  </>
                )}
              </p>
            )}
            {conversations.map((c) => (
              <button
                key={`${c.channel}:${c.peer}`}
                onClick={() => open(c.subject.id, c.channel, c.peer)}
                className={`w-full text-left p-4 border-b hover:bg-gray-50 transition-colors ${
                  subjectId === c.subject.id ? 'bg-blue-50' : ''
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h3 className="font-semibold text-gray-900 truncate">{c.subject.displayName}</h3>
                  <span className="text-xs text-gray-500 whitespace-nowrap">
                    {c.last_message_at ? moment.unix(c.last_message_at).fromNow() : ''}
                  </span>
                </div>
                <p className="text-sm text-gray-600 truncate">{c.last_message_preview || ''}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium border ${channelOf(c.channel)?.style}`}>
                    {channelOf(c.channel)?.label}
                  </span>
                  {c.unread_count > 0 && (
                    <span className="min-w-5 h-5 px-1 bg-green-500 rounded-full flex items-center justify-center text-xs text-white font-semibold">
                      {c.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))}
            {newProspects.length > 0 && (
              <>
                <p className="px-4 pt-4 pb-2 text-xs font-medium uppercase tracking-wide text-gray-500">Start a conversation</p>
                {newProspects.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => open(s.id)}
                    className={`w-full text-left px-4 py-3 border-b hover:bg-gray-50 ${subjectId === s.id ? 'bg-blue-50' : ''}`}
                  >
                    <h3 className="font-medium text-gray-900 truncate">{s.displayName}</h3>
                    <p className="text-xs text-gray-500 truncate">{[s.phone, s.email].filter(Boolean).join(' · ') || 'No phone or email'}</p>
                  </button>
                ))}
              </>
            )}
          </div>
        </div>

        <div className="flex-1 flex flex-col bg-gray-50">
          {subjectId && prospect ? (
            <>
              <div className="bg-white border-b px-6 py-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="font-semibold text-gray-900 truncate">{prospect.displayName}</h2>
                  <p className="text-sm text-gray-500 truncate">{[prospect.phone, prospect.email].filter(Boolean).join(' · ')}</p>
                  {prospect.doNotContact && <p className="text-sm text-red-600 mt-1">Marked do-not-contact. Nothing can be sent.</p>}
                  {prospect.phone && !prospect.doNotContact && (
                    <p className="text-sm text-gray-600 mt-1">
                      {prospect.whatsapp?.onWhatsApp === true && 'On WhatsApp'}
                      {prospect.whatsapp?.onWhatsApp === false && 'Not on WhatsApp'}
                      {prospect.whatsapp && prospect.whatsapp.onWhatsApp === null && 'Could not tell if they are on WhatsApp'}
                      {!prospect.whatsapp && 'WhatsApp not checked yet'}
                      {prospect.whatsapp && (
                        <span className="text-gray-400">
                          {' '}
                          ({prospect.whatsapp.via === 'message' ? 'they wrote to us' : 'checked'} {moment.unix(prospect.whatsapp.checkedAt).fromNow()})
                        </span>
                      )}{' '}
                      <button
                        type="button"
                        className="text-blue-600 hover:underline disabled:opacity-50"
                        disabled={recheck.isPending}
                        onClick={() =>
                          recheck.mutate(prospect.id, {
                            onError: (e) => toast.error(getApiErrorMessage(e, 'Could not check WhatsApp')),
                          })
                        }
                      >
                        {recheck.isPending ? 'Checking...' : 'Check again'}
                      </button>
                    </p>
                  )}
                </div>
                <Link to={`/admin/sales/prospects/${prospect.id}`} className="text-sm text-blue-600 hover:underline whitespace-nowrap">
                  Open prospect
                </Link>
              </div>

              <div className="flex-1 overflow-y-auto p-6 space-y-4">
                {messages.length === 0 && !thread.isLoading && <p className="text-sm text-gray-500 text-center">No messages yet</p>}
                {messages.map((m, index) => {
                  const out = m.direction === 'outbound';
                  return (
                    <div key={index} className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-md px-4 py-2 rounded-lg ${out ? 'bg-green-500 text-white' : 'bg-white text-gray-900 border border-gray-200'}`}>
                        <span
                          className={`inline-flex mb-1 px-1.5 py-0.5 rounded text-[11px] font-medium border ${
                            m.logged ? LOGGED_STYLE : channelOf(m.channel)?.style || LOGGED_STYLE
                          }`}
                        >
                          {m.logged ? `Logged · ${m.channel}` : channelOf(m.channel)?.label}
                          {!m.logged && labelOf(kindOf(m.channel), m.peer) && ` · ${labelOf(kindOf(m.channel), m.peer)}`}
                        </span>
                        {m.subject && <p className="text-sm font-semibold mb-1">{m.subject}</p>}
                        <p className="text-sm whitespace-pre-wrap">{m.body}</p>
                        <p className={`text-xs mt-1 text-right ${out ? 'text-white/70' : 'text-gray-500'}`}>{messageTime(m.at)}</p>
                      </div>
                    </div>
                  );
                })}
                <div ref={endRef} />
              </div>

              <div className="bg-white border-t px-6 py-4 space-y-3">
                <div className="flex flex-wrap gap-2" role="group" aria-label="Send on">
                  {CHANNELS.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => { setChannel(c.id); setFrom(''); }}
                      aria-pressed={channel === c.id}
                      title={check(c.id).ok ? 'Can send now' : check(c.id).error_description}
                      className={`${chip(channel === c.id)} inline-flex items-center gap-1.5`}
                    >
                      <span
                        aria-hidden="true"
                        className={`h-1.5 w-1.5 rounded-full ${check(c.id).ok ? 'bg-green-500' : 'bg-amber-500'}`}
                      />
                      {c.label}
                      <span className="sr-only">{check(c.id).ok ? '(can send)' : '(blocked)'}</span>
                    </button>
                  ))}
                </div>
                {/* Shown even when the channel is blocked, so the rep sees who it would go to. */}
                <div className="grid gap-2 sm:grid-cols-2">
                  {choices.length > 1 ? (
                    <select aria-label="Send to" value={target} onChange={(e) => setTo(e.target.value)} className={selectClass}>
                      {choices.map((r) => (
                        <option key={r.value} value={r.value}>
                          To {r.label}: {shown(kind, r.value)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    choices[0] && (
                      <p className="text-sm text-gray-600 self-center">
                        To {shown(kind, choices[0].value)}
                      </p>
                    )
                  )}
                  {fromChoices?.options.length === 1 && (
                    <p className="text-sm text-gray-600 self-center">From {fromChoices.options[0].label}</p>
                  )}
                  {fromChoices && fromChoices.options.length > 1 && (
                    <select aria-label="Send from" value={sender} onChange={(e) => setFrom(e.target.value)} className={selectClass}>
                      {fromChoices.options.map((o) => (
                        <option key={o.id} value={o.id}>
                          From {o.label}
                          {o.id === fromChoices.default ? ' (default)' : ''}
                        </option>
                      ))}
                    </select>
                  )}
                  {channel === 'sms' && smsLines.length > 0 && (
                    <select aria-label="Send from" value={from} onChange={(e) => setFrom(e.target.value)} className={selectClass}>
                      <option value="">From the line they last wrote to</option>
                      {smsLines.map((l) => (
                        <option key={l.id} value={l.id}>
                          From {smsLineLabel(l)}
                        </option>
                      ))}
                    </select>
                  )}
                  {channel === 'sms' && smsLines.length === 0 && (
                    <p className="text-sm text-gray-600 self-center">From FeelyFeely (SMS replies are not received yet)</p>
                  )}
                </div>
                {channel === 'email' && from && (
                  <SignatureToggle mailbox={mailboxes.find((m) => m.email === sender)} on={withSignature} onChange={setWithSignature} preview={false} />
                )}
                {outsideHours && current.ok && (
                  <p className="text-sm text-gray-600">
                    Outside {prospect.displayName}'s preferred contact hours ({hours!.start}-{hours!.end} Accra).
                  </p>
                )}
                {!current.ok ? (
                  <p className="text-sm text-amber-700">
                    {channelOf(channel)?.label}: {current.error_description}
                    {current.retryAt && ` Allowed again ${moment(current.retryAt).fromNow()}.`}
                    {current.error === 'not_claimed' && (
                      <>
                        {' '}
                        <Link to={`/admin/sales/prospects/${prospect.id}`} className="underline">
                          Claim them on the prospect page
                        </Link>
                        .
                      </>
                    )}
                    {current.error === 'no_consent' && (
                      <>
                        {' '}
                        <Link to={`/admin/sales/prospects/${prospect.id}`} className="underline">
                          Record their consent
                        </Link>{' '}
                        if they agreed to hear from us.
                      </>
                    )}
                  </p>
                ) : (
                  <>
                    {channel === 'email' && (
                      <input
                        type="text"
                        aria-label="Subject"
                        placeholder="Subject"
                        value={subjectLine}
                        onChange={(e) => setSubjectLine(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    )}
                    <div className="flex items-end gap-3">
                      {channel === 'email' ? (
                        <div className="flex-1">
                          <EmailEditor
                            key={draftKey}
                            value=""
                            minHeight={120}
                            placeholder="Write your email... (sent from the sales mailbox, with its signature)"
                            onChange={(c) => {
                              setBody(c.text);
                              setHtml(c.html);
                            }}
                          />
                        </div>
                      ) : (
                        <textarea
                          rows={2}
                          aria-label="Message"
                          placeholder={`Message by ${channelOf(channel)?.label}...`}
                          value={body}
                          onChange={(e) => setBody(e.target.value)}
                          className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                        />
                      )}
                      <button
                        onClick={send}
                        disabled={sendMessage.isPending || !body.trim() || !current.ok}
                        aria-label="Send message"
                        className="p-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                      >
                        <Send className="w-5 h-5" />
                      </button>
                    </div>
                  </>
                )}
              </div>
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center">
                <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Info className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {subjectId && thread.isLoading ? 'Loading...' : 'No prospect selected'}
                </h3>
                <p className="text-sm text-gray-500">Pick a conversation, or search for a prospect to write to</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </SalesPageShell>
  );
};

export default SalesConversationsPage;
